/**
 * Slice 19d — collapsed-by-default list of templates that opens any row in the
 * editor on click. Each row is a single line: name + sign-off chip / linked-posts
 * progress + remove. Heavy details (body preview, archetype chip strip, slot/example
 * counts) moved into the editor — keeps the list scannable when there are many
 * templates.
 */
import {
  deleteAdsTemplate,
  type AdsAssistantPostTagDefRow,
  type AdsAssistantTemplateRow,
} from "@/lib/adsAssistantClient";
import { AdsLibraryTemplateSignoffPanel } from "@/components/ads-assistant/AdsLibraryTemplateSignoffPanel";

type Props = {
  templates: AdsAssistantTemplateRow[];
  canEdit: boolean;
  postTagDefs: AdsAssistantPostTagDefRow[];
  editingTemplateId: string | null;
  onEdit: (t: AdsAssistantTemplateRow) => void;
  setError: (e: string | null) => void;
  setBanner: (b: string | null) => void;
  load: () => Promise<void>;
};

export function AdsLibraryTemplatesList({
  templates,
  canEdit,
  editingTemplateId,
  onEdit,
  setError,
  setBanner,
  load,
}: Props) {
  const active = templates.filter((t) => t.is_active);

  return (
    <details className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-zinc-900">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-700 select-none [&::-webkit-details-marker]:hidden dark:text-gray-200">
        <span>Templates list ({active.length})</span>
        <span className="text-[11px] text-gray-500">▾</span>
      </summary>
      <ul className="max-h-72 space-y-1 overflow-y-auto border-t border-gray-200 p-2 text-sm dark:border-gray-700">
        {!active.length ? <li className="text-xs text-gray-500">No templates yet.</li> : null}
        {active.map((t) => {
          const isOpen = t.id === editingTemplateId;
          return (
            <li
              key={t.id}
              className={
                "flex flex-wrap items-center justify-between gap-2 rounded border px-2 py-1 " +
                (isOpen
                  ? "border-blue-300 bg-blue-50/60 dark:border-blue-700 dark:bg-blue-950/30"
                  : "border-gray-200 bg-white dark:border-gray-700 dark:bg-zinc-950")
              }
            >
              <button
                type="button"
                onClick={() => onEdit(t)}
                className="min-w-0 flex-1 truncate text-left font-medium text-blue-700 hover:underline disabled:cursor-not-allowed disabled:opacity-60 dark:text-blue-300"
                disabled={!canEdit}
                title={canEdit ? "Edit template" : "Read-only"}
              >
                {t.name}
              </button>
              <span className="flex flex-wrap items-center gap-2 shrink-0">
                <AdsLibraryTemplateSignoffPanel
                  template={t}
                  canEdit={canEdit}
                  setError={setError}
                  setBanner={setBanner}
                  onChanged={load}
                />
                {canEdit ? (
                  <button
                    type="button"
                    className="text-[10px] text-red-700 hover:underline"
                    onClick={async () => {
                      const r = await deleteAdsTemplate(t.id);
                      if (!r.ok) {
                        setError(r.error);
                        return;
                      }
                      setBanner("Template removed.");
                      void load();
                    }}
                  >
                    Remove
                  </button>
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>
    </details>
  );
}
